/**
 * `136:950`'s "Se você salvar" card (`304:12261`): what the edit on screen
 * would do to the tenant's daily burn and runway, visible before the tenant
 * presses save rather than only inside `ReplanConfirmation`.
 *
 * `preview` is `null` while the replan preview has not answered, distinct
 * from `affected: 0` once it has. The runway clause shares
 * `ReplanConfirmation`'s `runwayDates`, so it drops out whole when either
 * end of the window is unknown, including when the funded-window fetch
 * failed. A zero delta (the edit on screen equals the stored rows, which is
 * true on page load) gets its own state rather than the burn and runway
 * sentences comparing a figure with itself.
 *
 * Under the headline sits the cost breakdown of the profile on its current, unsaved settings,
 * as a table. It describes the profile as edited, not the change, so it shows whenever a
 * preview exists, including when nothing uses the profile or the edit changes nothing.
 */

import { AlertCircle, TrendingDown, TrendingUp } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import { defineCatalogue, getLocale } from '../../../../i18n';
import { credits, integer } from '../../../../i18n/format';
import type { ApiFailure } from '../../../../lib/http/errors';
import { Skeleton } from '../../../../ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../ui/table';
import { cardsCatalogue } from '../../editor/cards.messages';
import { runwayDates, signedCredits } from './ReplanConfirmation';

const impactCatalogue = defineCatalogue(
  {
    title: 'Se você salvar',
    'headline.one': '{delta} crédito por dia',
    'headline.other': '{delta} créditos por dia',
    'burn.one': 'Em {count} conta: de {current} para {projected} por dia.',
    'burn.other': 'Somando as {count} contas: de {current} para {projected} por dia.',
    runway: 'Com o saldo de hoje, a coleta chega a {projected} em vez de {current}.',
    empty: 'Nenhuma conta usa este perfil.',
    unchanged: 'Esta alteração não muda o consumo diário das contas afetadas.',
    incomplete: 'Defina a cadência de cada tipo de mídia ligado para ver o impacto.',
    // Never `failureSentence(failure)`: for an `invalid` kind that returns the api's own English
    // text verbatim (`lib/http/errors.ts`'s `toInvalid`), which a tenant cannot act on.
    previewFailed: 'Não foi possível calcular o impacto agora. Tente de novo.',
    'breakdown.caption': 'Custo do perfil nas configurações atuais',
    'breakdown.item': 'Item',
    'breakdown.perDay': 'Créditos/dia',
    'breakdown.perAccount': 'Por conta/dia',
    'breakdown.total.one': 'Total para {count} conta/dia',
    'breakdown.total.other': 'Total para {count} contas/dia',
    'segment.posts': 'Métricas',
    'segment.stories': 'Varreduras',
    'segment.comments': 'Comentários',
    'segment.likers': 'Curtidores',
    'segment.analysis': 'Análise',
    'segment.audio': 'Reusos do áudio',
    'basis.worst_case': 'estimativa máxima — ainda sem dados suficientes',
    'basis.measured.one': 'com base em {count} coleta',
    'basis.measured.other': 'com base em {count} coletas',
    unpriced: 'sem preço',
    'unpriced.headline': 'Há itens deste perfil sem preço, então o impacto não pode ser somado.',
  },
  {
    title: 'If you save',
    'headline.one': '{delta} credit per day',
    'headline.other': '{delta} credits per day',
    'burn.one': 'On {count} account: from {current} to {projected} per day.',
    'burn.other': 'Across the {count} accounts: from {current} to {projected} per day.',
    runway: "At today's balance, collection reaches {projected} instead of {current}.",
    empty: 'No account uses this profile.',
    unchanged: "This change does not affect the accounts' daily consumption.",
    incomplete: 'Set the cadence of every media kind that is on to see the impact.',
    previewFailed: 'Could not calculate the impact right now. Try again.',
    'breakdown.caption': "The profile's cost on its current settings",
    'breakdown.item': 'Item',
    'breakdown.perDay': 'Credits/day',
    'breakdown.perAccount': 'Per account/day',
    'breakdown.total.one': 'Total for {count} account/day',
    'breakdown.total.other': 'Total for {count} accounts/day',
    'segment.posts': 'Metrics',
    'segment.stories': 'Sweeps',
    'segment.comments': 'Comments',
    'segment.likers': 'Likers',
    'segment.analysis': 'Analysis',
    'segment.audio': 'Audio reuses',
    'basis.worst_case': 'upper estimate — not enough data yet',
    'basis.measured.one': 'based on {count} collection',
    'basis.measured.other': 'based on {count} collections',
    unpriced: 'no price',
    'unpriced.headline':
      'Some items on this profile have no price, so the impact cannot be added up.',
  },
);
const sit = impactCatalogue.t;
const sitPlural = impactCatalogue.plural;

export type BreakdownSegment = 'posts' | 'stories' | 'comments' | 'likers' | 'analysis' | 'audio';

export interface SaveImpactPreview {
  affected: number;
  /** One account on the profile as edited; the lines sum to `credits_per_day`. */
  breakdown: {
    credits_per_day: number | null;
    lines: {
      media_kind: 'POST' | 'REEL' | 'STORY' | 'AUDIO';
      segment: BreakdownSegment;
      /** `null` where an operation the line runs has no rate. */
      credits_per_day: number | null;
      /** A likers line's basis and the comment pulls behind it; `null` on every other line. */
      basis: 'measured' | 'worst_case' | null;
      sample_pulls: number | null;
    }[];
    /** The operations with no rate. */
    unpriced: string[];
  };
  current_credits_per_day: number;
  projected_credits_per_day: number;
  delta_credits_per_day: number;
  /** The operations with no rate, on either side of the edit. */
  unpriced: string[];
  funded_until: string | null;
}

function Body(props: {
  preview: SaveImpactPreview | null;
  previewFailure: ApiFailure | null;
  /** An enabled kind names no schedule or tier yet: no preview is asked for. */
  incomplete: boolean;
  currentFundedUntil: string | null;
}): ReactNode {
  const locale = getLocale();
  const { preview } = props;

  if (props.incomplete) {
    return <p className="text-caption text-muted-foreground">{sit('incomplete')}</p>;
  }
  if (props.previewFailure !== null) {
    // Not the shared `ErrorState`, and not `failureSentence`: this card's own
    // copy, never the api's raw English text (see `previewFailed` above).
    return (
      <p role="alert" className="flex items-start gap-2 text-caption text-destructive">
        <AlertCircle aria-hidden className="size-4 shrink-0" />
        {sit('previewFailed')}
      </p>
    );
  }
  if (preview === null) {
    return (
      <div data-testid="save-impact-skeleton" className="flex flex-col gap-2">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (preview.affected === 0) {
    return <p className="text-caption text-muted-foreground">{sit('empty')}</p>;
  }

  if (preview.unpriced.length > 0) {
    return (
      <p role="alert" className="flex items-start gap-2 text-caption text-destructive">
        <AlertCircle aria-hidden className="size-4 shrink-0" />
        {sit('unpriced.headline')}
      </p>
    );
  }
  const delta = preview.delta_credits_per_day;
  // The edit on screen is a no-op: comparing the burn or the runway with
  // itself would read as a change this edit did not cause.
  if (delta === 0) {
    return <p className="text-caption text-muted-foreground">{sit('unchanged')}</p>;
  }
  const Icon = delta > 0 ? TrendingUp : TrendingDown;
  const dates = runwayDates(preview.funded_until, props.currentFundedUntil, locale);
  const burn = sitPlural('burn.other', preview.affected, {
    count: preview.affected,
    current: credits(preview.current_credits_per_day, locale),
    projected: credits(preview.projected_credits_per_day, locale),
  });

  return (
    <div className="flex flex-col gap-2">
      <p className="flex items-center gap-2 text-title-sm">
        <Icon aria-hidden className="size-4 shrink-0 text-primary-ink" />
        {sitPlural('headline.other', Math.abs(delta), { delta: signedCredits(delta, locale) })}
      </p>
      <p className="text-caption text-muted-foreground">
        {dates === null ? burn : `${burn} ${sit('runway', dates)}`}
      </p>
    </div>
  );
}

/** The kind and the step it is priced by, named once when they read the same. */
function lineLabel(line: SaveImpactPreview['breakdown']['lines'][number]): string {
  const kind = cardsCatalogue.t(`kind.${line.media_kind}`);
  const segment = sit(`segment.${line.segment}`);
  return kind === segment ? kind : `${kind} · ${segment}`;
}

function basisLabel(line: SaveImpactPreview['breakdown']['lines'][number]): string | null {
  if (line.basis === 'worst_case') return sit('basis.worst_case');
  if (line.basis === 'measured' && line.sample_pulls !== null) {
    return sitPlural('basis.measured.other', line.sample_pulls, {
      count: integer(line.sample_pulls, getLocale()),
    });
  }
  return null;
}

function Breakdown(props: { preview: SaveImpactPreview }): ReactNode {
  const locale = getLocale();
  const { breakdown, affected } = props.preview;
  return (
    <Table data-testid="save-impact-breakdown" label={sit('breakdown.caption')}>
      <TableCaption className="sr-only">{sit('breakdown.caption')}</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead>{sit('breakdown.item')}</TableHead>
          <TableHead className="text-right">{sit('breakdown.perDay')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {breakdown.lines.map((line) => {
          const basis = basisLabel(line);
          return (
            <TableRow key={`${line.media_kind}.${line.segment}`}>
              <TableCell>
                {lineLabel(line)}
                {basis !== null && (
                  <>
                    {' '}
                    <span className="block whitespace-normal text-caption text-muted-foreground">
                      {basis}
                    </span>
                  </>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {line.credits_per_day === null
                  ? sit('unpriced')
                  : credits(line.credits_per_day, locale)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell>{sit('breakdown.perAccount')}</TableCell>
          <TableCell className="text-right tabular-nums">
            {breakdown.credits_per_day === null
              ? sit('unpriced')
              : credits(breakdown.credits_per_day, locale)}
          </TableCell>
        </TableRow>
        {affected > 0 && breakdown.credits_per_day !== null && (
          <TableRow>
            <TableCell>
              {sitPlural('breakdown.total.other', affected, { count: affected })}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {credits(props.preview.projected_credits_per_day, locale)}
            </TableCell>
          </TableRow>
        )}
      </TableFooter>
    </Table>
  );
}

export function SaveImpactCard(props: {
  preview: SaveImpactPreview | null;
  previewFailure: ApiFailure | null;
  /** An enabled kind names no schedule or tier yet: no preview is asked for. */
  incomplete: boolean;
  currentFundedUntil: string | null;
}): ReactNode {
  const titleId = useId();
  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-2">
      <h2 id={titleId} className="text-title-sm">
        {sit('title')}
      </h2>
      <Body {...props} />
      {!props.incomplete && props.previewFailure === null && props.preview !== null && (
        <Breakdown preview={props.preview} />
      )}
    </section>
  );
}
